/**
 * EmptyState — the house empty-state card: a bordered --control-bg block
 * with a 13px-medium statement of what's absent over a NOTE_CLASS line
 * saying how it comes to exist (or how to recover), plus an optional
 * action row. Codifies the pattern the admin pages and lens bodies were
 * each hand-copying.
 *
 * `useTenantEmpty` + `ConnectEndpointsAction` let a first-use empty state
 * on an endpoint-derived surface tell the truth about WHY it's empty: on
 * a tenant with no endpoints ever reporting, "nothing in this scope" is
 * misdirection — the tenant is unenrolled and the fix is the Connect
 * surface. Gate the hook on "this surface is actually rendering empty"
 * so the connect-state fetch is only spent when the answer matters.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { NOTE_CLASS } from "../lib/ui.js";
import { useConnectState } from "../lib/connectState.js";
import { listDirectoryIntegrations } from "../lib/identityClient.js";
import { hasActiveDirectory } from "../pages/dataSources/model.js";

export default function EmptyState({ title, note = null, action = null, className = "" }) {
    return (
        <div className={`grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px] ${className}`.trim()}>
            <strong className="text-[13px] leading-[1.08] font-medium">{title}</strong>
            {note ? <span className={NOTE_CLASS}>{note}</span> : null}
            {action ? <div className="mt-[4px] flex">{action}</div> : null}
        </div>
    );
}

// True only on a CONFIRMED zero-endpoint tenant — unknown/loading/denied
// all read false, so a surface never claims "not connected" on a hunch.
// Pass `enabled: false` while the surface has rows or is still loading;
// the connect-state fetch fires only once the empty state is real.
export function useTenantEmpty({ enabled = true } = {}) {
    const connect = useConnectState({ enabled });
    return connect.status === "ready" && connect.connected === false;
}

export function useNoDirectory({ enabled = true } = {}) {
    const [state, setState] = useState(null);
    useEffect(() => {
        if (!enabled || state !== null) return undefined;
        let live = true;
        listDirectoryIntegrations().then(
            (response) => {
                if (live) setState(hasActiveDirectory(response) ? "connected" : "none");
            },
            () => {
                if (live) setState("unknown");
            },
        );
        return () => {
            live = false;
        };
    }, [enabled, state]);
    return enabled && state === "none";
}

export function connectDirectoryParams(prev) {
    const next = new URLSearchParams(prev);
    next.set("mode", "admin");
    next.set("adminView", "directory");
    next.delete("settingsView");
    next.delete("chat");
    next.delete("cv");
    return next;
}

export function ConnectDirectoryAction({ label = "Connect identity provider" }) {
    const [searchParams, setSearchParams] = useSearchParams();
    return (
        <button
            type="button"
            onClick={() => setSearchParams(connectDirectoryParams(searchParams))}
            className="h-[26px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[9px] text-[10px] leading-[1.08] tracking-[0.04em] uppercase font-medium text-[color:var(--ink)] cursor-pointer"
        >
            {label}
        </button>
    );
}

// The URL mutation behind ConnectEndpointsAction, pure for tests: land on
// the Endpoints > Connect view, leaving unrelated params (range, tabs) alone.
// Clears ?chat / ?cv so returning to dashboard mode re-evaluates its
// front door rather than restoring a stale sub-view.
export function connectViewParams(prev) {
    const next = new URLSearchParams(prev);
    next.set("mode", "endpoints");
    next.set("adminView", "connect");
    next.delete("settingsView");
    next.delete("chat");
    next.delete("cv");
    return next;
}

export function ConnectEndpointsAction({ label = "Connect endpoints" }) {
    const [searchParams, setSearchParams] = useSearchParams();
    return (
        <button
            type="button"
            onClick={() => setSearchParams(connectViewParams(searchParams))}
            className="h-[26px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[9px] text-[10px] leading-[1.08] tracking-[0.04em] uppercase font-medium text-[color:var(--ink)] cursor-pointer"
        >
            {label}
        </button>
    );
}
